import React, { useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import BusinessIcon from "@mui/icons-material/Business";
import LogoutIcon from "@mui/icons-material/Logout";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EditIcon from "@mui/icons-material/Edit";
import { CreditScore as CreditScoreIcon } from "@mui/icons-material";
import PersonAddIcon from "@mui/icons-material/PersonAdd"; // <-- NEW IMPORT
import { useNavigate, useLocation } from "react-router-dom";
import GroupIcon from "@mui/icons-material/Group";
import DescriptionIcon from "@mui/icons-material/Description";

const drawerWidth = 240;

const ResponsiveLayout = ({
  currentUser,
  allUsers = [],
  onImpersonate,
  onLogout,
  companyName = "Card Vision",
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // --- NEW: Conditionally add "Create User" to menu for admins ---
  const menuItems = [
    { text: "Scanner", icon: <DashboardIcon />, path: "/scanner" },
    { text: "Scanned Cards", icon: <CreditScoreIcon />, path: "/history" },
    { text: "Edit Details", icon: <EditIcon />, path: "/company-details" },
    { text: "Templates", icon: <DescriptionIcon />, path: "/templates" },
    { text: "Team", icon: <GroupIcon />, path: "/team" },
  ];
  if (currentUser?.is_super_admin) {
    menuItems.push({
      text: "Create User",
      icon: <PersonAddIcon />,
      path: "/create-user",
    });
  }

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar>
        <Typography variant="h6" fontWeight={600}>
          Card Vision
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map(({ text, icon, path }) => (
          <ListItem key={text} disablePadding>
            <ListItemButton
              selected={location.pathname === path}
              onClick={() => {
                navigate(path);
                setDrawerOpen(false);
              }}
            >
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText primary={text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={onLogout}>
            <ListItemIcon>
              <LogoutIcon color="error" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <BusinessIcon sx={{ mr: 1 }} />
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            {companyName}
          </Typography>

          {/* --- Super Admin Impersonation Dropdown --- */}
          {currentUser?.is_super_admin && !currentUser?.original_user_id
            ? location.pathname === "/company-details" && (
              <FormControl
                size="small"
                sx={{ m: 1, minWidth: 180, mr: 2 }}
                variant="outlined"
              >
                <InputLabel
                  id="impersonate-select-label"
                  sx={{ color: "white", "&.Mui-focused": { color: "white" } }}
                >
                  Select User
                </InputLabel>
                <Select
                  labelId="impersonate-select-label"
                  value=""
                  label="View User"
                  onChange={(e) => onImpersonate(e.target.value)}
                  sx={{
                    color: "white",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "white",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "white",
                    },
                    ".MuiSvgIcon-root": { color: "white" },
                  }}
                >
                  {allUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )
            : null}
          {currentUser?.is_super_admin && !currentUser?.original_user_id
            ? location.pathname === "/company-details" && (
              <Typography
                variant="body2"
                sx={{ mr: 2, display: { xs: "none", sm: "block" } }}
              >
                <strong>
                  {currentUser?.impersonating_as || currentUser?.username}
                </strong>
              </Typography>
            )
            : null}

          <Tooltip title="Logout">
            <IconButton color="inherit" onClick={onLogout}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{ "& .MuiDrawer-paper": { width: drawerWidth } }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default ResponsiveLayout;
